import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { WALLET_TOPUP_POLL_MS } from 'modules/organizer/domain/constants/wallet-topup';
import { isWalletTopupTerminal } from 'modules/organizer/domain/data-mappers/wallet-topup.data-mapper';
import { TournamentPlan } from 'modules/organizer/domain/interfaces/tournament-plan';
import {
  useBuyPlanMutation,
  useCreateWalletTopupMutation,
  useGetMyWalletQuery,
  useGetTournamentPlansQuery,
  useGetWalletTopupQuery,
} from 'modules/organizer/store/billing-api';
import { useGetActiveTournamentPlanQuery } from 'modules/organizer/store/organizer-plan-api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { baseApi } from 'shared/api/base-api';
import { useAppDispatch, useAppSelector } from 'store/hooks';

export interface CreateTopupArgs {
  amountInMinorUnits: number;
}

export interface BuyPlanArgs {
  planId: number;
}

export function useSubscriptionBilling() {
  const dispatch = useAppDispatch();
  const tournamentId = useAppSelector(authStoreUserTournamentId);
  const [pendingTopupId, setPendingTopupId] = useState<number | null>(null);
  const [isTopupTerminal, setIsTopupTerminal] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [buyError, setBuyError] = useState<string | null>(null);

  const walletQuery = useGetMyWalletQuery(tournamentId as number, { skip: !tournamentId });
  const plansQuery = useGetTournamentPlansQuery(undefined, { skip: !tournamentId });
  const activePlanQuery = useGetActiveTournamentPlanQuery({ tournamentId: tournamentId as number }, { skip: !tournamentId });
  const [createTopup, createState] = useCreateWalletTopupMutation();
  const [buyPlan, buyState] = useBuyPlanMutation();
  const pendingTopupQuery = useGetWalletTopupQuery(pendingTopupId ?? 0, {
    skip: pendingTopupId == null,
    pollingInterval: isTopupTerminal ? 0 : WALLET_TOPUP_POLL_MS,
  });

  const plans = plansQuery.data ?? [];
  const activePlan = activePlanQuery.data ?? null;
  const defaultPlan = useMemo(() => pickDefaultPlan(plans, activePlan?.id), [plans, activePlan?.id]);

  useEffect(() => {
    const status = pendingTopupQuery.data?.status;
    setIsTopupTerminal(!!status && isWalletTopupTerminal(status));
  }, [pendingTopupQuery.data?.status]);

  useEffect(() => {
    setIsTopupTerminal(false);
  }, [pendingTopupId]);

  useEffect(() => {
    if (pendingTopupQuery.data?.status !== 'CONFIRMED' || !tournamentId) {
      return;
    }

    dispatch(baseApi.util.invalidateTags(['Wallet', { type: 'Organizer', id: tournamentId }]));
  }, [dispatch, pendingTopupQuery.data?.status, tournamentId]);

  const startTopup = useCallback(
    async ({ amountInMinorUnits }: CreateTopupArgs) => {
      if (!tournamentId) {
        return;
      }

      setSubmitError(null);
      try {
        const topup = await createTopup({ tournamentId, amountInMinorUnits }).unwrap();
        setPendingTopupId(topup.id);
      } catch (error) {
        setSubmitError(extractErrorMessage(error, 'Не удалось создать платёж. Попробуйте ещё раз.'));
      }
    },
    [createTopup, tournamentId],
  );

  const payPlan = useCallback(
    async ({ planId }: BuyPlanArgs) => {
      if (!tournamentId) {
        return;
      }

      setBuyError(null);
      try {
        await buyPlan({
          tournamentId,
          planId,
          switchPlan: false,
          createRecurring: false,
        }).unwrap();
      } catch (error) {
        setBuyError(extractErrorMessage(error, 'Не удалось оплатить тариф. Пополните баланс и попробуйте ещё раз.'));
      }
    },
    [buyPlan, tournamentId],
  );

  const resetTopup = useCallback(() => {
    setPendingTopupId(null);
    setSubmitError(null);
    setBuyError(null);
    setIsTopupTerminal(false);
  }, []);

  return {
    tournamentId,
    wallet: walletQuery.data ?? null,
    plans,
    activePlan,
    defaultPlan,
    pendingTopup: pendingTopupId == null ? null : pendingTopupQuery.data ?? null,
    isLoading: walletQuery.isLoading || plansQuery.isLoading || (!!tournamentId && activePlanQuery.isLoading),
    isCreating: createState.isLoading,
    isBuying: buyState.isLoading,
    submitError,
    buyError,
    startTopup,
    payPlan,
    resetTopup,
  };
}

function pickDefaultPlan(plans: TournamentPlan[], activePlanId?: number): TournamentPlan | null {
  if (activePlanId) {
    return plans.find((plan) => plan.id === activePlanId) ?? plans[0] ?? null;
  }

  return plans[0] ?? null;
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message) {
    if (error.message === 'Not enough money') {
      return 'Недостаточно средств на балансе';
    }

    return error.message;
  }

  return fallback;
}
